/**
 * @file components/audit-tasks/task-list-helpers.ts
 * @description 稽/審核流程管理列表（/audit-tasks）的純函式工具（2026-09-25 回報序48）。
 *
 * - `deriveReceiveStatus`：由派案回覆 + 任務狀態推導「接收狀態」（取代舊「狀態」欄）。
 *   舊版建任務即寫「已確認」，致未派案確認的任務顯示「已確認」；現以派案回覆
 *   dispatchResponse 為準，並相容舊資料。
 * - `getCompletionDueState`：稽核完成日（稽核任務書完成時間）距今天數與「7 天內紅色提示」判定。
 */

import dayjs from 'dayjs';

/** 接收狀態：待接收 / 已接收 / 不接收 */
export type ReceiveStatus = '待接收' | '已接收' | '不接收';

/** 推導接收狀態所需的任務欄位 */
export interface ReceiveStatusInput {
  /** 任務狀態（audit_tasks.status） */
  status: string;
  /** 派案回覆：accepted / rejected / null（未回覆） */
  dispatchResponse: string | null;
  /** 現場報到時間 */
  arrivedAt: string | null;
  /** 計畫月份（舊版確認流程會寫入，用於相容無 dispatchResponse 的舊資料） */
  plannedAuditMonth: string | null;
}

/** 已進入確認後流程的任務狀態（必定已接收） */
const POST_CONFIRM_STATUSES = new Set(['計畫中', '現場中', '報告中', '完成']);

/**
 * 推導任務的「接收狀態」。
 *
 * 規則（優先序）：
 *  1. dispatchResponse='rejected' 或 status='退回改派' → 不接收
 *  2. dispatchResponse='accepted' → 已接收
 *  3. 已進入計畫/現場/報告/完成、或已現場報到 → 已接收（舊資料無 dispatchResponse）
 *  4. status='已確認' 且有計畫月份（舊版確認流程）→ 已接收
 *  5. 其餘 → 待接收
 *
 * @param t 任務欄位
 */
export function deriveReceiveStatus(t: ReceiveStatusInput): ReceiveStatus {
  if (t.dispatchResponse === 'rejected' || t.status === '退回改派') return '不接收';
  if (t.dispatchResponse === 'accepted') return '已接收';
  if (POST_CONFIRM_STATUSES.has(t.status) || t.arrivedAt) return '已接收';
  if (t.status === '已確認' && t.plannedAuditMonth) return '已接收';
  return '待接收';
}

/** 稽核完成日提示判定結果 */
export interface CompletionDueState {
  /** 距稽核完成日天數（負數＝已逾期）；無日期為 null */
  daysLeft: number | null;
  /** 是否以紅色提示（到期前 7 天內或已逾期，且任務未完成） */
  alert: boolean;
}

/** 稽核完成日提前紅色提示的天數（到達完成日前 7 天起） */
export const COMPLETION_ALERT_DAYS = 7;

/**
 * 判定稽核完成日是否需紅色提示。
 *
 * 需求：「在到達稽核完成日 7 天前，系統自動以紅色提示於表列」——即距完成日 ≤ 7 天
 * （含當天與已逾期）且任務尚未完成時提示；任務「完成」後不再提示。
 *
 * @param completionDate 稽核完成日（YYYY-MM-DD）；null 表示未設定
 * @param taskStatus     任務狀態
 * @param today          今天（測試注入；預設 dayjs()）
 */
export function getCompletionDueState(
  completionDate: string | null,
  taskStatus: string,
  today: dayjs.Dayjs = dayjs(),
): CompletionDueState {
  if (!completionDate) return { daysLeft: null, alert: false };
  const due = dayjs(completionDate.slice(0, 10));
  if (!due.isValid()) return { daysLeft: null, alert: false };
  const daysLeft = due.startOf('day').diff(today.startOf('day'), 'day');
  const alert = taskStatus !== '完成' && daysLeft <= COMPLETION_ALERT_DAYS;
  return { daysLeft, alert };
}
